import React, { useState, useEffect, useRef } from 'react';
import { RotateCw } from 'lucide-react';

const PRIZE_KEY = 'valentine_prize';

const setCookie = (name, value, days = 365) => {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  const encodedValue = encodeURIComponent(value);
  const secure =
    typeof window !== 'undefined' && window.location.protocol === 'https:'
      ? ';Secure'
      : '';
  document.cookie = `${name}=${encodedValue};expires=${expires.toUTCString()};max-age=${days * 24 * 60 * 60};path=/;SameSite=Lax${secure}`;
};

const getCookie = (name) => {
  const nameEQ = name + '=';
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) {
      const raw = c.substring(nameEQ.length, c.length);
      try {
        return decodeURIComponent(raw);
      } catch {
        return raw;
      }
    }
  }
  return null;
};

const setStoredPrize = (value) => {
  try {
    window.localStorage?.setItem(PRIZE_KEY, value);
  } catch {}
  setCookie(PRIZE_KEY, value);
};

const getStoredPrize = () => {
  try {
    const fromStorage = window.localStorage?.getItem(PRIZE_KEY);
    if (fromStorage) return fromStorage;
  } catch {}
  return getCookie(PRIZE_KEY);
};

const WheelOfFortune = () => {
  const [isSpinning, setIsSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [prize, setPrize] = useState(null);
  const [hasWon, setHasWon] = useState(false);
  const wheelRef = useRef(null);

  // Define wheel segments with their probabilities
  const segments = [
    { value: '50', probability: 25, color: '#ff6b9d', size: 1 },
    { value: 'Riprova', probability: 25, color: '#ffc1d5', size: 1 },
    { value: '99', probability: 10, color: '#e91e63', size: 0.7 },
    { value: '52', probability: 25, color: '#ff8fab', size: 1 },
    { value: '88', probability: 11, color: '#d81b60', size: 0.6 },
    { value: '188', probability: 4, color: '#c2185b', size: 0.3 }
  ];

  // Calculate total size units for visual segments
  const totalSize = segments.reduce((sum, seg) => sum + seg.size, 0);

  // Calculate angle for each segment based on size
  const segmentAngles = segments.map(seg => (seg.size / totalSize) * 360);

  // Check cookie on mount
  useEffect(() => {
    const wonPrize = getStoredPrize();
    if (wonPrize && wonPrize !== 'Riprova') {
      setHasWon(true);
      setPrize(wonPrize);
    }
  }, []);

  // Select prize based on probability
  const selectPrize = () => {
    const random = Math.random() * 100;
    let cumulativeProbability = 0;

    for (const segment of segments) {
      cumulativeProbability += segment.probability;
      if (random <= cumulativeProbability) {
        return segment;
      }
    }
    return segments[0]; // Fallback
  };

  // Calculate rotation for selected segment
  const getRotationForSegment = (segmentIndex) => {
    // Calculate cumulative angle to the center of the target segment
    let cumulativeAngle = 0;
    for (let i = 0; i < segmentIndex; i++) {
      cumulativeAngle += segmentAngles[i];
    }
    // Add half of the target segment's angle to get to its center
    cumulativeAngle += segmentAngles[segmentIndex] / 2;
    
    const spins = 5; // Number of full rotations
    // Rotate so the segment center aligns with the top pointer (subtract from 360)
    return 360 * spins + (360 - cumulativeAngle);
  };

  const spinWheel = () => {
    if (isSpinning || hasWon) return;

    setIsSpinning(true);
    setPrize(null);

    // Select winning segment
    const winningSegment = selectPrize();
    const segmentIndex = segments.findIndex(s => s.value === winningSegment.value);
    
    // Normalize current rotation to 0-360 range
    const normalizedRotation = rotation % 360;
    
    // Calculate rotation for the winning segment
    const targetRotation = getRotationForSegment(segmentIndex);
    
    // Add current normalized rotation to maintain continuity
    const newRotation = rotation - normalizedRotation + targetRotation;
    
    setRotation(newRotation);

    // After animation completes
    setTimeout(() => {
      setIsSpinning(false);
      setPrize(winningSegment.value);

      // If won a prize (not Retry), save to cookie and disable
      if (winningSegment.value !== 'Riprova') {
        setStoredPrize(winningSegment.value);
        setHasWon(true);
      }
    }, 4000);
  };

  return (
    <div className="wheel-section">
      <div className="wheel-header">
        <h2 className="wheel-title">Gira la ruota dell'amore!</h2>
        <p className="wheel-subtitle">Prova la tua fortuna e guadagna un Red-Pocket</p>
      </div>

      <div className="wheel-container">
        <div className="wheel-backdrop" aria-hidden="true">
          <img
            className="wheel-backdrop-svg"
            src={`${process.env.PUBLIC_URL}/ui/Ferriswheel.svg`}
            alt=""
          />
        </div>

        <div className="wheel-foreground">
          <div className="wheel-spin-area">
            <div className="wheel-pointer"></div>

            <svg
              ref={wheelRef}
              className="wheel"
              viewBox="0 0 200 200"
              style={{
                transform: `rotate(${rotation}deg)`,
                transition: isSpinning ? 'transform 4s cubic-bezier(0.17, 0.67, 0.12, 0.99)' : 'none'
              }}
            >
              {segments.map((segment, index) => {
                let startAngle = 0;
                for (let i = 0; i < index; i++) {
                  startAngle += segmentAngles[i];
                }
                const segmentAngle = segmentAngles[index];

                const startRad = (startAngle - 90) * Math.PI / 180;
                const endRad = (startAngle + segmentAngle - 90) * Math.PI / 180;

                const x1 = 100 + 100 * Math.cos(startRad);
                const y1 = 100 + 100 * Math.sin(startRad);
                const x2 = 100 + 100 * Math.cos(endRad);
                const y2 = 100 + 100 * Math.sin(endRad);

                const largeArc = segmentAngle > 180 ? 1 : 0;

                const pathData = `M 100 100 L ${x1} ${y1} A 100 100 0 ${largeArc} 1 ${x2} ${y2} Z`;

                const textAngle = startAngle + segmentAngle / 2;
                const textRad = (textAngle - 90) * Math.PI / 180;
                const textX = 100 + 65 * Math.cos(textRad);
                const textY = 100 + 65 * Math.sin(textRad);

                return (
                  <g key={index}>
                    <path
                      d={pathData}
                      fill={segment.color}
                      stroke="#fff"
                      strokeWidth="2"
                    />
                    <text
                      x={textX}
                      y={textY}
                      textAnchor="middle"
                      dominantBaseline="middle"
                      fill="white"
                      fontSize="16"
                      fontWeight="600"
                      fontFamily="'Playfair Display', serif"
                      style={{ textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)' }}
                      transform={`rotate(${textAngle}, ${textX}, ${textY})`}
                    >
                      {segment.value}
                    </text>
                  </g>
                );
              })}
            </svg>
          </div>
        </div>
      </div>

      {/* Spin Button */}
      <button
        className={`spin-button ${isSpinning || hasWon ? 'disabled' : ''}`}
        onClick={spinWheel}
        disabled={isSpinning || hasWon}
      >
        <RotateCw className={`spin-icon ${isSpinning ? 'spinning' : ''}`} size={20} />
        {isSpinning ? 'Spinning...' : hasWon ? 'Già girato' : 'Gira la ruota'}
      </button>

      {/* Result Message */}
      {prize && (
        <div className="wheel-result">
          {prize === 'Riprova' ? (
            <p className="result-text retry">
              Prova ancora! Gira di nuovo!
            </p>
          ) : (
            <p className="result-text winner">
              Congratulazioni! Hai vinto €{prize}!
            </p>
          )}
        </div>
      )}

      {hasWon && prize !== 'Riprova' && (
        <div className="winner-message">
          <p className="winner-subtitle">
            Il tuo Red-Pocket di €<strong>{prize}</strong> è stato salvato, comunicalo ad uno dei tuoi husbands!
          </p>
        </div>
      )}
    </div>
  );
};

export default WheelOfFortune;
